/* eslint-disable @typescript-eslint/naming-convention */
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  Observable, Subject, debounceTime, distinctUntilChanged,
  map, startWith, switchMap, takeUntil
} from 'rxjs';

import { InternalReferralService, } from '@sondermind/data-access/provider-facing/internal-referral';
import { ReferenceDataHttpService, ReferenceDataTypes, ReferenceDatum } from '@sondermind/data-access/reference-data';
import { FilterOptionsStore } from '@sondermind/data-access/public-facing/filter-form';
import { ISpecialty } from '@sondermind/utilities/models-provider-preview';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisModalRef } from '@sondermind/iris/modal';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { IconSizes } from '@sondermind/iris/icon';

import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { SegmentService } from '@sondermind/segment';
import { InternalReferralEligibility, Patient } from '@sondermind/utilities/models-patient';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { BreakpointsService } from '@sondermind/utilities/services';
import { InternalReferralTypes } from '@sondermind/utilities/models-referrals';

export type PsychiatryModalInputType = {
  patient: Patient;
  provider: ProviderUser;
  eligibilityData: InternalReferralEligibility;
};

type psychiatryFormType = {
  reasonForReferral: string;
  diagnosis?: ReferenceDatum;
  preferences?: string;
};
type psychiatryTypeControls = { [key in keyof psychiatryFormType]: AbstractControl };
type psychiatryFormGroup = FormGroup & { value: psychiatryFormType; controls: psychiatryTypeControls; };

@Component({
  selector: 'psychiatry-modal',
  templateUrl: './psychiatry-modal.component.html',
  styleUrls: ['./psychiatry-modal.component.scss'],
  standalone: false
})
export class PsychiatryModalComponent implements OnInit, OnDestroy {
  readonly iconSizes = IconSizes;
  saving = false;
  progressBarValue = 50;
  currentStep = 1;
  patient: Patient;
  provider: ProviderUser;
  selectedDiagnosis: ReferenceDatum[] = [];

  separatorKeysCodes: number[] = [ENTER, COMMA];

  referralFormGroup: FormGroup = new FormGroup({
    reasonForReferral: new FormControl('', Validators.required),
    diagnosis: new FormControl(undefined),
    specialty: new FormControl(undefined),
    preferences: new FormControl('')
  }) as psychiatryFormGroup;
  confirmationFormGroup: FormGroup = new FormGroup({
    spoken: new FormControl(false, Validators.requiredTrue),
    accurate: new FormControl(false, Validators.requiredTrue)
  });
  @ViewChild('diagnosisInput') diagnosisInput: ElementRef;
  @ViewChild('specialtiesInput') specialtiesInput: ElementRef;

  filterOptions$: Observable<ReferenceDatum[]>;
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  specialtiesData: ISpecialty[] = [];
  filteredSpecialtiesData: ISpecialty[] = [];
  selectedSpecialties: ISpecialty[] = [];
  headerTitle: string = 'Refer client to psychiatry';
  eligibilityData: InternalReferralEligibility;
  referralType: string;
  destroy$: Subject<void> = new Subject<void>();

  constructor(
    private breakpointService: BreakpointsService,
    private irisModalRef: IrisModalRef<PsychiatryModalComponent, PsychiatryModalInputType, boolean>,
    private refDataService: ReferenceDataHttpService,
    private internalReferralService: InternalReferralService,
    private segmentService: SegmentService<SondermindEvents>,
    private snackbar: MatSnackBar,
    private filtersStore: FilterOptionsStore
  ) {
    this.filterOptions$ = this.referralFormGroup.controls.diagnosis.valueChanges
      .pipe(
        startWith(''),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((val) => this.search(val || ''))
      );

    this.referralFormGroup.controls.specialty.valueChanges.pipe(startWith(''),
      distinctUntilChanged(),
      debounceTime(400))
      .subscribe((value) => {
        this.searchTherapyTopic(value);
      });
  }
  ngOnInit(): void {
    this.patient = this.irisModalRef.config?.data?.patient || null;
    this.provider = this.irisModalRef.config?.data?.provider || null;
    this.breakpointService.toggleModal(true);
    this.getSpecialties();
    this.eligibilityData = this.irisModalRef.config?.data?.eligibilityData;
    if (this.eligibilityData.psychReferral) {
      this.headerTitle = 'Refer client to therapy';
      this.referralFormGroup.get('specialty').setValidators(Validators.required);
      this.referralType = InternalReferralTypes.THERAPY;
    } else {
      this.referralFormGroup.get('specialty').clearValidators();
      this.referralType = InternalReferralTypes.PSYCHIATRY;
    }
  }
  ngOnDestroy(): void {
    this.breakpointService.toggleModal(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Searches for title, tag, and description
  search(searchString: string): Observable<ReferenceDatum[]> {
    return this.refDataService
      .list(ReferenceDataTypes.idc_codes,
        {
          page: 'disabled',
          filter: [{ key: 'search', value: searchString }, { key: 'hidden', value: 'false' }],
          sort: [{ key: 'tag', asc: true }]
        })
      .pipe(
        map((idc_codes) => idc_codes.data)
      );
  }

  changeStep(step: number) {
    this.currentStep = step;
    this.progressBarValue = this.currentStep * 50;
    this.progressBarValue > 100 ? this.progressBarValue = 100 : null;
  }

  removeDiagnosis(diagnosis: ReferenceDatum): void {
    const index = this.selectedDiagnosis.indexOf(diagnosis);

    if (index >= 0) {
      this.selectedDiagnosis.splice(index, 1);
    }
  }

  removeSpecialty(value: ISpecialty): void {
    const index = this.selectedSpecialties.indexOf(value);

    if (index >= 0) {
      this.selectedSpecialties.splice(index, 1);
    }

    if (!this.selectedSpecialties.length) {
      this.referralFormGroup.controls.specialty.setValue(null);
    }
  }

  diagnosisSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedDiagnosis.push(event.option.value);
    this.diagnosisInput.nativeElement.value = '';
    this.referralFormGroup.controls.diagnosis.setValue(null);
  }

  specialtiesSelected(value: ISpecialty): void {
    this.selectedSpecialties.push(value);
    this.specialtiesInput.nativeElement.value = '';
    this.specialtiesData = this.filteredSpecialtiesData;
  }

  close(): void {
    this.irisModalRef.close(false);
  }

  submit(): void {
    this.saving = true;
    const segEvent = {
      providerId: this.provider.personaProviderId,
      providerName: this.provider.fullName,
      providerEmail: this.provider.email,
      referralName: this.patient.fullName,
      referralPersonaClientId: this.patient.personaClientId,
      referralEmail: this.patient.email,
      referringType: this.referralType,
      dateViewed: new Date().toISOString()
    };
    this.internalReferralService.submitReferral({
      internal_referral: {
        persona_client_id: this.patient.personaClientId,
        referring_type: this.referralType,
        referring_reason: this.referralFormGroup.controls.reasonForReferral.value,
        preferences: this.referralFormGroup.controls.preferences.value,
        reference_data_ids: this.selectedDiagnosis.map((d) => d.id),
        reasons_ids: this.selectedSpecialties.map((d) => d.id)
      }
    }).subscribe({
      next: (res: any) => {
        this.saving = false;
        if (!res.data) {
          this.snackbar.open('Failed to submit referral. Try again later.');
          this.segmentService.track<SondermindEvent.InternalReferralFailed>(
            SondermindEvent.InternalReferralFailed,
            segEvent
          );
          this.close();
          return;
        }
        this.segmentService.track<SondermindEvent.InternalReferralSubmitted>(
          SondermindEvent.InternalReferralSubmitted,
          segEvent
        );
        this.changeStep(3);
      },
      error: (err) => {
        this.snackbar.open('Failed to submit referral. Try again later.');
        this.segmentService.track<SondermindEvent.InternalReferralFailed>(
          SondermindEvent.InternalReferralFailed,
          segEvent
        );
        this.close();
      }
    });
  }

  getSpecialties(): void {
    this.filtersStore.specialtyList$().pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.specialtiesData = res;
      this.filteredSpecialtiesData = res;
    });
  }

  searchTherapyTopic(searchString: any): void {
    if (searchString?.id) return;
    const filterValue = searchString.toLowerCase();
    this.specialtiesData = this.filteredSpecialtiesData.filter((option) => option?.title?.toLowerCase()?.includes(filterValue)
    );
  }
}
